import { IconProp } from "@fortawesome/fontawesome-svg-core";
import { faEdit } from "@fortawesome/free-solid-svg-icons/faEdit";
import { faTrashAlt } from "@fortawesome/free-solid-svg-icons/faTrashAlt";
import React from "react";
import { useTranslation } from "react-i18next";
import styled from "styled-components";

import { AtiraIcon } from "../../../../../components/AtiraIcon";
import { Button } from "../../../../../components/Button";
import { Flex } from "../../../../../components/Flex";
import { AtiraImage } from "../../../../../components/Image";
import { FontSize, Text } from "../../../../../components/Text";
import { RoadmapItem } from "../../../../../model/roadmap/RoadmapItem";
import { RoadmapItemStatus } from "../../../../../model/roadmap/types/RoadmapItemStatus.enum";
import { AppLangs } from "../../../../../model/shared/enum/AppLangs.enum";
import { Breakpoints } from "../../../../../theme/Breakpoints";
import { Rounded } from "../../../../../theme/Rounded";
import { Shadow } from "../../../../../theme/Shadow";
import { Spacing } from "../../../../../theme/Spacing";
import { ThemeType } from "../../../../../theme/Theme";
import { getFontAwesomeIcon } from "../../../../../utils/getFontAwesomeIcon";
import { TestIDs } from "../../../../../tests/types/testIds";
import { getMediaUrl } from "../../../../../utils/Media";

/** Mirrors the colours the public roadmap page uses for each status */
const STATUS_COLOR: Record<RoadmapItemStatus, keyof ThemeType> = {
  [RoadmapItemStatus.DONE]: "main",
  [RoadmapItemStatus.IN_PROGRESS]: "darkerSub",
  [RoadmapItemStatus.UPCOMING]: "accent",
};

const CardContainer = styled.div<{ statusColor: keyof ThemeType }>`
  position: relative;
  background: ${({ theme }) => theme.white};
  border: 1px solid ${({ theme }) => theme.sub};
  border-inline-start: 5px solid
    ${({ theme, statusColor }) => theme[statusColor]};
  border-radius: ${Rounded.xl};
  padding: ${Spacing.l};
  display: flex;
  flex-direction: column;
  gap: ${Spacing.m};
  box-shadow: ${Shadow.SM};
`;

const HeaderRow = styled(Flex)`
  justify-content: space-between;
  align-items: center;
  flex-wrap: wrap;
  gap: ${Spacing.s};
`;

const StatusChip = styled.span<{ statusColor: keyof ThemeType }>`
  background: ${({ theme, statusColor }) => theme[statusColor]};
  color: ${({ theme }) => theme.white};
  padding: ${Spacing.xs} ${Spacing.m};
  border-radius: ${Rounded.pill};
  font-size: ${FontSize.xs};
  font-weight: 600;
  white-space: nowrap;
`;

const OrderBadge = styled.span`
  font-family: monospace;
  font-size: calc(${FontSize.xs} - 0.1rem);
  background: ${({ theme }) => theme.lightSub};
  color: ${({ theme }) => theme.darkerSub};
  padding: ${Spacing.xs} ${Spacing.s};
  border-radius: ${Rounded.sm};
`;

const ImageContainer = styled.div`
  width: 50%;
  height: 12rem;
  border-radius: ${Rounded.lg};
  overflow: hidden;
  border: 1px solid ${({ theme }) => theme.sub};

  .ant-image,
  .ant-image-img {
    width: 100%;
    height: 100%;
    object-fit: cover;
  }

  @media (min-width: ${Breakpoints.DESKTOP}) {
    width: 30%;
  }
`;

const LangBlock = styled(Flex)`
  flex-direction: column;
  gap: ${Spacing.xs};
  background: ${({ theme }) => theme.lightSub};
  padding: ${Spacing.m};
  border-radius: ${Rounded.md};
  flex: 1;
  min-width: 15rem;

  ul {
    margin: 0;
    padding-inline-start: ${Spacing.l};
  }

  li {
    font-size: ${FontSize.s};
    color: ${({ theme }) => theme.lightBlack};
  }
`;

const CardFooter = styled(Flex)`
  margin-top: auto;
  padding-top: ${Spacing.m};
  border-top: 1px solid ${({ theme }) => theme.sub};
  gap: ${Spacing.s};
`;

const ActionButton = styled(Button)<{ variant?: keyof ThemeType }>`
  margin: 0;
  width: 12rem;
  background: transparent;
  border: 1px solid ${({ theme, variant }) => theme[variant || "main"]};
  color: ${({ theme, variant }) => theme[variant || "main"]};

  &:hover:not(:disabled) {
    background: ${({ theme, variant }) => theme[variant || "main"]};
    color: ${({ theme }) => theme.white};
  }
`;

type AdminRoadmapItemCardProps = {
  roadmapItem: RoadmapItem;
  onEdit: VoidFunction;
  onDelete: VoidFunction;
};

export const AdminRoadmapItemCard: React.FC<AdminRoadmapItemCardProps> = ({
  roadmapItem,
  onEdit,
  onDelete,
}) => {
  const { t } = useTranslation();

  const statusColor = STATUS_COLOR[roadmapItem.status];
  const icon = getFontAwesomeIcon(roadmapItem.icon);
  const image = roadmapItem.images?.[0];

  return (
    <CardContainer
      statusColor={statusColor}
      data-testid={TestIDs.roadmap.card.wrapper(roadmapItem._id)}
    >
      <HeaderRow>
        <Flex alignItems="center" gap="s" flexWrap="wrap">
          <StatusChip statusColor={statusColor}>
            {t(`admin.roadmap.status.${roadmapItem.status.toLowerCase()}`)}
          </StatusChip>

          {roadmapItem.period ? (
            <Text fontSize="xs" color="darkerSub">
              {roadmapItem.period}
            </Text>
          ) : null}

          <OrderBadge>
            {t("admin.roadmap.drawer.order.title")}: {roadmapItem.order}
          </OrderBadge>
        </Flex>
      </HeaderRow>

      <Flex alignItems="center" gap="s">
        {icon ? (
          <AtiraIcon icon={icon as IconProp} color="main" size="lg" />
        ) : null}

        <Text fontWeight="bold" color="lightBlack">
          {roadmapItem.title?.[AppLangs.EN]}
        </Text>
      </Flex>

      {image ? (
        <ImageContainer>
          <AtiraImage preview src={getMediaUrl(image)} />
        </ImageContainer>
      ) : null}

      <Flex gap="m" flexWrap="wrap">
        {Object.values(AppLangs).map((lang) => {
          const bullets = roadmapItem.descriptions?.[lang] || [];
          const title = roadmapItem.title?.[lang];

          if (!title && !bullets.length) {
            return null;
          }

          return (
            <LangBlock key={lang}>
              <Text fontSize="xs" color="darkerSub" fontWeight="bold">
                {t(`common.lang.${lang}`).toUpperCase()}
              </Text>

              {title ? <Text fontSize="s">{title}</Text> : null}

              {bullets.length ? (
                <ul>
                  {bullets.map((bullet, index) => (
                    <li key={index}>{bullet}</li>
                  ))}
                </ul>
              ) : null}
            </LangBlock>
          );
        })}
      </Flex>

      <CardFooter>
        <ActionButton
          variant="accent"
          icon={faEdit}
          onClick={onEdit}
          dataTestId={TestIDs.roadmap.card.edit(roadmapItem._id)}
        >
          {t("common.edit")}
        </ActionButton>

        <ActionButton
          variant="danger"
          icon={faTrashAlt}
          onClick={onDelete}
          dataTestId={TestIDs.roadmap.card.delete(roadmapItem._id)}
        >
          {t("common.delete")}
        </ActionButton>
      </CardFooter>
    </CardContainer>
  );
};
